/**
 * KAN-909: значение фильтра по менеджеру с умолчанием "свой менеджер" и
 * запомненным выбором (правило в lib/managerFilterChoice). Общий для всех мест
 * с выбором менеджера; место передаёт свои значения "Все" и "этот менеджер" и
 * явное значение из адреса или пресета (оно главнее запомненного).
 * choose запоминает выбор (и "Все менеджеры"), clear забывает его (снова умолчание).
 * useDropUnknownManager: выбранный удалённый менеджер сбрасывается (Заказы, Лиды по
 * теплоте, Клиенты; CRM сбрасывает по ответу 400, useDropRejectedCrmManager).
 */
import { useCallback, useEffect, useState } from 'react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  clearManagerChoice,
  defaultManagerFilter,
  isUnknownManager,
  loadManagerChoice,
  ownManagerId,
  resolveManagerFilter,
  saveManagerChoice,
  type ManagerChoiceScope,
  type ManagerChoiceValues,
} from '@/lib/managerFilterChoice';

export interface ManagerFilterChoice {
  /** Значение фильтра сейчас. */
  value: string;
  /** Умолчание места (для "фильтр задан" и сброса). */
  defaultValue: string;
  /** Своя запись справочника менеджера у входа; null - нет. */
  ownManagerId: string | null;
  choose: (value: string) => void;
  clear: () => void;
}

interface StoredChoice {
  userId: string | undefined;
  value: string | null;
}

export function useManagerFilterChoice(
  scope: ManagerChoiceScope,
  values: ManagerChoiceValues,
  explicit?: string | null
): ManagerFilterChoice {
  const { data: user } = useCurrentUser();
  const userId = user?.id;
  const ownId = ownManagerId(user);
  const [choice, setChoice] = useState<StoredChoice>(() => ({ userId, value: loadManagerChoice(scope, userId) }));
  // Пользователь загрузился или сменился: выбор читается из хранилища под его ключом.
  const stored = choice.userId === userId ? choice.value : loadManagerChoice(scope, userId);

  const choose = useCallback(
    (value: string) => {
      saveManagerChoice(scope, userId, value);
      setChoice({ userId, value });
    },
    [scope, userId]
  );
  const clear = useCallback(() => {
    clearManagerChoice(scope, userId);
    setChoice({ userId, value: null });
  }, [scope, userId]);

  return {
    value: resolveManagerFilter(explicit, stored, ownId, values),
    defaultValue: defaultManagerFilter(ownId, values),
    ownManagerId: ownId,
    choose,
    clear,
  };
}

/** Выбранного менеджера нет в загруженном списке (удалён): reset (обычно clear выбора). */
export function useDropUnknownManager(
  value: string,
  managers: readonly { id: string }[] | undefined,
  special: readonly string[],
  reset: () => void
): void {
  const unknown = isUnknownManager(value, managers, special);
  useEffect(() => {
    if (unknown) reset();
  }, [unknown, reset]);
}
